import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { CloseOutlined } from '@ant-design/icons';
import { CLOUD_FRONT_URL } from '../../aws.config';
import { selectedProductAtom } from '../../state';
import { useRecoilValue } from 'recoil';

interface IDownloadModal {
  isOpen: boolean;
  onClose: () => void;
  title: string;
}

const DownloadModal = ({ isOpen, onClose, title }: IDownloadModal) => {
  const [availableFormats, setAvailableFormats] = useState<any[]>([]);
  const [selectedFormat, setSelectedFormat] = useState('');
  const product: any = useRecoilValue(selectedProductAtom);

  // Build available formats based on product data
  useEffect(() => {
    if (!product) return;

    const formats = [];

    // 1. DAE format (hardcoded as first option)
    if (product.uploadedDaeUrl) {
      formats.push({
        id: 'dae',
        name: 'DAE',
        url: `${CLOUD_FRONT_URL}${product.uploadedDaeUrl}`
      });
    }

    // 2. OBJ or FBX (only one shown based on availability)
    if (product.objUrl) {
      formats.push({
        id: 'obj',
        name: 'OBJ',
        url: `${CLOUD_FRONT_URL}${product.objUrl}`
      });
    } else if (product.fbxUrl) {
      formats.push({
        id: 'fbx',
        name: 'FBX',
        url: `${CLOUD_FRONT_URL}${product.fbxUrl}`
      });
    }

    // 3. GLTF (always shown as third option)
    if (product.styledGltfUrl) {
      formats.push({
        id: 'gltf',
        name: 'GLTF',
        url: `${CLOUD_FRONT_URL}${product.styledGltfUrl}`
      });
    }

    setAvailableFormats(formats);
    if (formats.length > 0) {
      setSelectedFormat(formats[0].id);
    }
  }, [product]);

  const handleDownload = async () => {
    if (!selectedFormat || availableFormats.length === 0) return;

    const format = availableFormats.find(f => f.id === selectedFormat);
    if (!format || !format.url) return;

    try {
      const response = await fetch(format.url);
      if (!response.ok) throw new Error('Failed to download file');

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);

      const fileName = `${product.name.replace(/\s+/g, '_')}.${format.id}`;
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Error downloading file:', error);
      // Handle error (e.g., show toast notification)
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-center align-middle shadow-xl transition-all">
                <div className="flex justify-center items-center mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    {title}
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="flex items-center justify-center absolute right-4 rounded-full p-2 hover:bg-gray-100 transition-colors"
                  >
                    <CloseOutlined className="h-4 w-4 text-gray-500" />
                  </button>
                </div>

                <div className="mt-4">
                  <p className="text-gray-600 mb-6 text-center">
                    Please select 3D file you wish to download and
                    downloaded zip file contains the product texture files
                  </p>

                  <div className="space-y-4 w-full">
                    {availableFormats?.length > 0 ? (
                      availableFormats?.map((format) => (
                        <div key={format.id} className="flex items-center">
                          <div className="flex items-center h-5">
                            <input
                              id={format.id}
                              name="format"
                              type="radio"
                              checked={selectedFormat === format.id}
                              onChange={() => setSelectedFormat(format.id)}
                              className="h-4 w-4 border-gray-300 text-accent radio-rounded focus:outline-none focus:ring-0 focus:ring-offset-0 checked:bg-accent checked:border-accent hover:border-accent cursor-pointer"
                            />
                          </div>
                          <div className="ml-3 text-sm">
                            <label htmlFor={format.id} className="font-medium text-gray-900">
                              {format.name}
                            </label>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 py-4">No downloadable formats available</p>
                    )}
                  </div>

                  <div className="mt-6 flex justify-center">
                    <button
                      type="button"
                      className={`mt-5 inline-flex w-full justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 ${availableFormats.length === 0
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-accent hover:bg-accent-dark'
                        }`}
                      onClick={handleDownload}
                      disabled={availableFormats.length === 0}
                    >
                      Download File
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DownloadModal;